import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { useTranslation } from '@/i18n';
import type { MessageKey } from '@/i18n/messages';

/**
 * What a guest sees while the check loads, and when it cannot.
 *
 * Deliberately plain: this renders before the location is known, so there is no brand to render in. Painting
 * a half-branded screen and repainting it once the theme arrives is worse than one honest neutral one.
 */

export function LoadingScreen() {
    const { t } = useTranslation();

    // The location is not known on these screens, so the title stays generic — but still translated.
    useDocumentTitle(t('document.title'));

    return (
        <div className="min-h-dvh bg-muted" aria-busy="true" aria-live="polite">
            <div className="mx-auto w-full max-w-[var(--shell-max-width)]">
                <span className="sr-only">{t('status.loading')}</span>

                <header className="flex flex-col gap-2.5 px-5 pb-4 pt-9">
                    <Skeleton className="h-8 w-52" />
                    <Skeleton className="h-5 w-36" />
                </header>

                <main className="px-5">
                    <div className="overflow-hidden rounded-xl border border-border bg-card px-4 py-1 shadow-xs">
                        {/* Four rows is roughly a small check — enough to read as "a check is coming" without
                            promising a specific length. */}
                        {[0, 1, 2, 3].map((row) => (
                            <div key={row} className="flex items-center justify-between gap-4 py-3">
                                <Skeleton className="h-4 w-40" />
                                <Skeleton className="h-4 w-14" />
                            </div>
                        ))}
                    </div>
                </main>
            </div>
        </div>
    );
}

interface ErrorScreenProps {
    titleKey: MessageKey;
    detailKey: MessageKey;
    onRetry?: () => void;
}

export function ErrorScreen({ titleKey, detailKey, onRetry }: ErrorScreenProps) {
    const { t } = useTranslation();

    useDocumentTitle(t('document.title'));

    return (
        // `role="alert"` so a screen reader announces the failure rather than leaving a guest waiting on a
        // screen that silently stopped loading.
        <div
            role="alert"
            className="flex min-h-dvh flex-col items-center justify-center bg-muted px-6 text-center"
        >
            <div className="flex w-full max-w-sm flex-col items-center gap-3">
                <h1 className="text-xl font-semibold tracking-tight text-balance">{t(titleKey)}</h1>
                <p className="text-[0.9375rem] leading-relaxed text-muted-foreground text-pretty">
                    {t(detailKey)}
                </p>

                {onRetry && (
                    <Button variant="outline" className="mt-3 h-11 px-6" onClick={onRetry}>
                        {t('error.retry')}
                    </Button>
                )}
            </div>
        </div>
    );
}

/**
 * The message for every "this code will not open a check" case.
 *
 * The API cannot tell an unknown code from an expired one from a closed check, by design, so neither can
 * this. Rather than guess, it says what is true of all three and points at the one thing that always works:
 * the printed code in the guest's hand, and failing that, the server standing nearby.
 */
export function InvalidCodeScreen() {
    return <ErrorScreen titleKey="error.invalidCode.title" detailKey="error.invalidCode.detail" />;
}
